import { Asset, AssetState, DashboardStats, FileType } from '../types/asset';
import { PaginatedResponse } from '../types/api';
import { generateMockAssets } from '../utils/constants';
import { config } from '../config';
import { apiFetch, buildQueryString, getAuthToken } from './apiClient';

// Singleton mock store (used when config.api.useMock is true)
const mockAssets: Asset[] = generateMockAssets(150);

export const getAssets = async (
  filters: {
    search?: string;
    types?: FileType[];
    states?: AssetState[];
    page?: number;
    limit?: number;
  },
): Promise<{ data: Asset[]; total: number }> => {
  if (!config.api.useMock && getAuthToken()) {
    const qs = buildQueryString({
      search: filters.search,
      fileType: filters.types,
      state: filters.states,
      page: filters.page,
      limit: filters.limit,
    });
    const result = await apiFetch<PaginatedResponse<Asset>>(`/assets?${qs}`);
    return { data: result.data, total: result.total };
  }

  let filtered = [...mockAssets];

  if (filters.search) {
    const s = filters.search.toLowerCase();
    filtered = filtered.filter(a =>
      a.id.toLowerCase().includes(s) ||
      a.locationPath.toLowerCase().includes(s)
    );
  }

  if (filters.types && filters.types.length > 0) {
    filtered = filtered.filter(a => filters.types!.includes(a.fileType));
  }

  if (filters.states && filters.states.length > 0) {
    filtered = filtered.filter(a => filters.states!.includes(a.state));
  }

  filtered.sort((a, b) => b.receivedOn - a.receivedOn);

  const start = ((filters.page || 1) - 1) * (filters.limit || 10);
  const end = start + (filters.limit || 10);

  return {
    data: filtered.slice(start, end),
    total: filtered.length
  };
};

export const getAssetById = async (id: string): Promise<Asset | undefined> => {
  if (!config.api.useMock && getAuthToken()) {
    try {
      return await apiFetch<Asset>(`/assets/${id}`);
    } catch {
      return undefined;
    }
  }
  return mockAssets.find(a => a.id === id);
};

export const getDashboardStats = async (): Promise<DashboardStats> => {
  if (!config.api.useMock && getAuthToken()) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const [allResult, errorResult, todayResult, processingResult] = await Promise.all([
      apiFetch<PaginatedResponse<Asset>>('/assets?limit=1'),
      apiFetch<PaginatedResponse<Asset>>('/assets?state=ERROR&limit=1'),
      apiFetch<PaginatedResponse<Asset>>(`/assets?dateFrom=${today.toISOString()}&limit=1`),
      apiFetch<PaginatedResponse<Asset>>('/assets?state=INITIAL&state=IDENTIFIED&limit=1'),
    ]);

    return {
      totalAssets: allResult.total,
      receivedToday: todayResult.total,
      activeProcessing: processingResult.total,
      failedAssets: errorResult.total,
    };
  }

  const today = new Date().setHours(0, 0, 0, 0);
  return {
    totalAssets: mockAssets.length,
    receivedToday: mockAssets.filter(a => a.receivedOn >= today).length,
    activeProcessing: mockAssets.filter(a => a.state === AssetState.INITIAL || a.state === AssetState.IDENTIFIED).length,
    failedAssets: mockAssets.filter(a => a.state === AssetState.ERROR).length
  };
};

export const getRecentActivity = async (limit: number = 10): Promise<Asset[]> => {
  if (!config.api.useMock && getAuthToken()) {
    const result = await apiFetch<PaginatedResponse<Asset>>(`/assets?limit=${limit}`);
    return result.data;
  }

  return [...mockAssets]
    .sort((a, b) => b.receivedOn - a.receivedOn)
    .slice(0, limit);
};

export interface AssetActionResponse {
  actionId: string;
  assetId: string;
  type: string;
  triggeredAt: string;
}

export const triggerAction = async (
  assetId: string,
  actionType: string,
): Promise<AssetActionResponse> => {
  if (!config.api.useMock && getAuthToken()) {
    return apiFetch<AssetActionResponse>(`/assets/${assetId}/actions/${actionType}`, {
      method: 'POST',
    });
  }
  // Mock: simulate a successful trigger
  return {
    actionId: crypto.randomUUID(),
    assetId,
    type: actionType,
    triggeredAt: new Date().toISOString(),
  };
};
